import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Banknote, AlertCircle, Wallet, TrendingUp, Receipt, Calendar, Calculator } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface ForecastWeek {
  weekNumber: number;
  weekCommencing: string;
  monthKey: string;
  seasonalityIndex: number;
  activeEngineers: number;
  chargeableDays: number;
  existingReceipts: number;
  newEngineerBillings: number;
  totalReceipts: number;
  netWagesExisting: number;
  payeNicExisting: number;
  vatPayment: number;
  materialsExisting: number;
  materialsNew: number;
  marketingExisting: number;
  marketingNew: number;
  newEngineerLoadedCost: number;
  amexRepayment: number;
  barclaycardRepayment: number;
  barclaysLoan: number;
  pipeGoCardless: number;
  otherOverheads: number;
  directorDividends: number;
  totalPayments: number;
  netMovement: number;
  closingBalance: number;
}

interface ForecastMonth {
  monthKey: string;
  monthLabel: string;
  revenue: number;
  materials: number;
  fieldLabourAndVans: number;
  grossProfit: number;
  grossMarginPct: number | null;
  marketing: number;
  otherOverheadsPlusOfficePayroll: number;
  netProfit: number;
  netMarginPct: number | null;
  closingBalance: number;
}

interface ForecastSummary {
  modelStartDate: string;
  modelEndDate: string;
  openingBankBalance: number;
  closingBankBalance: number;
  fullYearRevenue: number;
  fullYearGrossProfit: number;
  fullYearNetProfit: number;
  totalWeeks: number;
}

interface SeasonedRateCalc {
  source: "live" | "fallback";
  lookbackMonths: number;
  windowStart: string;
  windowEnd: string;
  totalRevenue: number;
  existingEngineers: number;
  workingDaysPerMonth: number;
  workingDays: number;
  ratePerDay: number;
  acceptedOpportunities: number;
  notes: string;
}

interface LiveDerivation {
  windowStart: string;
  windowEnd: string;
  monthsCovered: number;
  invoiceCount: number;
  totalRevenue: number;
  totalMaterials: number;
  totalMarketing: number;
  totalWages: number;
  totalPayeNic: number;
  totalOverheadsOther: number;
  totalSubcontractor: number;
  deseasonalisedWeeklyReceipts: number;
  materialsPctOfRevenue: number;
  marketingPctOfRevenue: number;
  monthlyWages: number;
  monthlyPayeNic: number;
  monthlyOverheadsOther: number;
  seasonalityIndex: Record<string, number>;
  monthlyRevenue: Array<{ month: string; revenue: number; index: number }>;
  bucketBreakdown: Array<{ bucket: string; total: number; monthly_avg: number }>;
  openingBankBalance: number | null;
}

interface ForecastResponse {
  assumptions: Record<string, unknown>;
  defaultAssumptions: Record<string, unknown>;
  liveDerivation: LiveDerivation | null;
  inputs: { dayRateA: number; lookbackMonths: number; useLive: boolean };
  seasonedRateCalc: SeasonedRateCalc;
  scenarios: {
    a: { scenario: "A"; scenarioLabel: string; dayRate: number; weeks: ForecastWeek[]; monthly: ForecastMonth[]; summary: ForecastSummary };
    b: { scenario: "B"; scenarioLabel: string; dayRate: number; weeks: ForecastWeek[]; monthly: ForecastMonth[]; summary: ForecastSummary };
  };
  summary: { a: ForecastSummary; b: ForecastSummary };
}

function fmtGbp(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return "—";
  const sign = n < 0 ? "−" : "";
  const abs = Math.abs(n);
  return `${sign}£${abs.toLocaleString("en-GB", { maximumFractionDigits: 0 })}`;
}
function fmtPct(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return "—";
  return `${n.toFixed(1)}%`;
}
function fmtWk(iso: string): string {
  const d = new Date(iso + "T00:00:00Z");
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", timeZone: "UTC" });
}

export default function FinancialHub() {
  const [scenario, setScenario] = useState<"A" | "B">("A");
  const [dayRateA, setDayRateA] = useState<string>("695");
  const [lookbackMonths, setLookbackMonths] = useState<string>("2");
  // committedX are what the API was last queried with — only update on Apply
  // so typing in the day-rate field doesn't fire a query on every keystroke.
  const [committedDayRateA, setCommittedDayRateA] = useState<number>(695);
  const [committedLookbackMonths, setCommittedLookbackMonths] = useState<number>(2);

  const { data, isLoading, error } = useQuery<ForecastResponse>({
    queryKey: ["financial-hub-forecast", committedDayRateA, committedLookbackMonths],
    queryFn: async () => {
      const qs = new URLSearchParams({
        dayRateA: String(committedDayRateA),
        lookbackMonths: String(committedLookbackMonths),
      }).toString();
      const res = await fetch(`/api/financial-hub/forecast?${qs}`, { credentials: "include" });
      if (!res.ok) throw new Error(`${res.status}`);
      return res.json();
    },
  });

  const handleApply = () => {
    const a = Number(dayRateA);
    const lb = Number(lookbackMonths);
    if (Number.isFinite(a) && a > 0) setCommittedDayRateA(a);
    if (Number.isFinite(lb) && lb > 0) setCommittedLookbackMonths(lb);
  };

  if (isLoading) return <div className="p-6 text-muted-foreground">Loading cashflow forecast…</div>;
  if (error || !data) return <div className="p-6 text-red-600">Failed: {error instanceof Error ? error.message : "unknown"}</div>;

  const active = scenario === "A" ? data.scenarios.a : data.scenarios.b;
  const sA = data.summary.a;
  const sB = data.summary.b;

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Banknote className="h-6 w-6" /> Financial Hub — Cashflow Forecast to 31 Mar 2027
          </h1>
          <p className="text-sm text-muted-foreground">
            Weekly cashflow + monthly P&L based on the seasonality and growth model from your spreadsheet.
            Two scenarios compare conservative vs seasoned new-engineer day rates.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant={scenario === "A" ? "default" : "outline"}
            size="sm"
            onClick={() => setScenario("A")}
          >
            A · £{data.scenarios.a.dayRate}/day
          </Button>
          <Button
            variant={scenario === "B" ? "default" : "outline"}
            size="sm"
            onClick={() => setScenario("B")}
          >
            B · £{data.scenarios.b.dayRate}/day
          </Button>
        </div>
      </div>

      {/* Inputs row */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Calculator className="h-4 w-4" /> Inputs
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
            <div className="space-y-1.5">
              <Label htmlFor="dayRateA">Scenario A — new-engineer billing per day (£)</Label>
              <Input
                id="dayRateA"
                type="number"
                min={0}
                step={5}
                value={dayRateA}
                onChange={(e) => setDayRateA(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") handleApply(); }}
                data-testid="finhub-day-rate-a"
              />
              <p className="text-[11px] text-muted-foreground">
                Conservative new-starter assumption. Press Apply to re-run.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="lookback">Scenario B — lookback months for live calc</Label>
              <Select value={lookbackMonths} onValueChange={setLookbackMonths}>
                <SelectTrigger id="lookback" data-testid="finhub-lookback">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">Last 1 month</SelectItem>
                  <SelectItem value="2">Last 2 months</SelectItem>
                  <SelectItem value="3">Last 3 months</SelectItem>
                  <SelectItem value="6">Last 6 months</SelectItem>
                  <SelectItem value="12">Last 12 months</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground">
                Window over which Commusoft revenue is summed for the seasoned rate.
              </p>
            </div>
            <Button onClick={handleApply} data-testid="finhub-apply">Apply</Button>
          </div>
        </CardContent>
      </Card>

      {/* Live derivation of Scenario B's day rate */}
      <Card className="border-emerald-500/40 bg-emerald-50/30 dark:bg-emerald-950/10">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2 text-emerald-700 dark:text-emerald-300">
            <Calculator className="h-4 w-4" /> Scenario B day rate — live from Commusoft
          </CardTitle>
        </CardHeader>
        <CardContent className="text-sm">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Metric label="Day rate (B)" value={`£${data.seasonedRateCalc.ratePerDay.toLocaleString("en-GB", { maximumFractionDigits: 0 })}`} icon={<TrendingUp className="h-3.5 w-3.5" />} />
            <Metric label="Revenue in window" value={fmtGbp(data.seasonedRateCalc.totalRevenue)} icon={<Receipt className="h-3.5 w-3.5" />} />
            <Metric label="Window" value={`${data.seasonedRateCalc.windowStart} → ${data.seasonedRateCalc.windowEnd}`} icon={<Calendar className="h-3.5 w-3.5" />} />
            <Metric label="Engineers" value={String(data.seasonedRateCalc.existingEngineers)} icon={<Wallet className="h-3.5 w-3.5" />} />
            <Metric label="Working days" value={String(data.seasonedRateCalc.workingDays)} icon={<Calendar className="h-3.5 w-3.5" />} />
          </div>
          <div className="text-[11px] text-muted-foreground mt-3">
            <b>{data.seasonedRateCalc.source === "live" ? "Live" : "FALLBACK"}:</b> {data.seasonedRateCalc.notes}
            {" "}{data.seasonedRateCalc.acceptedOpportunities > 0 && (
              <>· {data.seasonedRateCalc.acceptedOpportunities} accepted invoices</>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Live derivation from Xero — every input the model needs that we can derive */}
      {data.liveDerivation && (
        <Card className="border-sky-500/40 bg-sky-50/30 dark:bg-sky-950/10">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2 text-sky-700 dark:text-sky-300">
              <Calculator className="h-4 w-4" /> Live derivation from Xero (trailing {data.liveDerivation.monthsCovered} months)
            </CardTitle>
            <p className="text-xs text-muted-foreground">
              Every input below is computed from <code className="font-mono">keystone_finance.invoices</code> line items joined to the Xero chart of accounts.
              Forward-planning inputs (day rates, hire schedule) stay manual.
            </p>
          </CardHeader>
          <CardContent className="space-y-4 text-sm">
            {/* Headline window */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Metric icon={<Calendar className="h-3.5 w-3.5" />} label="Window" value={`${data.liveDerivation.windowStart} → ${data.liveDerivation.windowEnd}`} />
              <Metric icon={<TrendingUp className="h-3.5 w-3.5" />} label="Total revenue (12mo)" value={fmtGbp(data.liveDerivation.totalRevenue)} />
              <Metric icon={<Receipt className="h-3.5 w-3.5" />} label="Deseasonalised weekly" value={fmtGbp(data.liveDerivation.deseasonalisedWeeklyReceipts)} />
              <Metric icon={<Wallet className="h-3.5 w-3.5" />} label="Months covered" value={String(data.liveDerivation.monthsCovered)} />
            </div>

            {/* Live ratio table */}
            <div>
              <div className="text-xs font-medium mb-1">Forecast inputs — live vs spreadsheet default</div>
              <table className="text-xs w-full">
                <thead className="text-left text-muted-foreground border-b">
                  <tr>
                    <th className="py-1 pr-2">Input</th>
                    <th className="text-right pr-2">12-mo total</th>
                    <th className="text-right pr-2">Monthly avg</th>
                    <th className="text-right pr-2">Live value</th>
                    <th className="text-right pr-2">Spreadsheet</th>
                    <th className="text-right">Δ</th>
                  </tr>
                </thead>
                <tbody>
                  <DerivedRow label="Materials % of revenue" total={data.liveDerivation.totalMaterials} liveValue={`${(data.liveDerivation.materialsPctOfRevenue * 100).toFixed(1)}%`} ssValue="28.0%" delta={(data.liveDerivation.materialsPctOfRevenue * 100 - 28).toFixed(1)} pctMode source="LIVE" />
                  <DerivedRow label="Marketing % of revenue" total={data.liveDerivation.totalMarketing} liveValue={`${(data.liveDerivation.marketingPctOfRevenue * 100).toFixed(2)}%`} ssValue="10.0%" delta={(data.liveDerivation.marketingPctOfRevenue * 100 - 10).toFixed(2)} pctMode source="LIVE" />
                  <DerivedRow label="Wages (monthly)" total={data.liveDerivation.totalWages} liveValue={data.liveDerivation.monthlyWages > 0 ? fmtGbp(data.liveDerivation.monthlyWages) : fmtGbp(27000)} ssValue={fmtGbp(27000)} delta={(data.liveDerivation.monthlyWages - 27000).toString()} source={data.liveDerivation.monthlyWages > 0 ? "LIVE" : "FALLBACK"} note="Not in Xero invoices — paid via separate payroll system" />
                  <DerivedRow label="PAYE/NIC (monthly)" total={data.liveDerivation.totalPayeNic} liveValue={data.liveDerivation.monthlyPayeNic > 0 ? fmtGbp(data.liveDerivation.monthlyPayeNic) : fmtGbp(8500)} ssValue={fmtGbp(8500)} delta={(data.liveDerivation.monthlyPayeNic - 8500).toString()} source={data.liveDerivation.monthlyPayeNic > 0 ? "LIVE" : "FALLBACK"} note="Same — payroll-system data not in BQ yet" />
                  <DerivedRow label="Other overheads (monthly)" total={data.liveDerivation.totalOverheadsOther} liveValue={fmtGbp(data.liveDerivation.monthlyOverheadsOther)} ssValue={fmtGbp(16000)} delta={(data.liveDerivation.monthlyOverheadsOther - 16000).toString()} source="LIVE" />
                </tbody>
              </table>
            </div>

            {/* Bucket breakdown */}
            <div>
              <div className="text-xs font-medium mb-1">All buckets (sanity check on coverage)</div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1 text-xs">
                {data.liveDerivation.bucketBreakdown.filter((b) => b.total !== 0).map((b) => (
                  <div key={b.bucket} className="flex justify-between border-b border-dashed border-muted py-0.5">
                    <span className="text-muted-foreground">{b.bucket.replace(/_/g, " ")}</span>
                    <span className="font-mono">{fmtGbp(b.total)}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Monthly revenue + seasonality */}
            <div>
              <div className="text-xs font-medium mb-1">Monthly revenue + seasonality index (1.0 = average)</div>
              <div className="overflow-x-auto">
                <table className="text-xs">
                  <thead className="text-muted-foreground">
                    <tr>
                      <th className="text-left pr-2"></th>
                      {data.liveDerivation.monthlyRevenue.map((m) => (
                        <th key={m.month} className="px-1.5 text-right whitespace-nowrap">{m.month.slice(5)}/{m.month.slice(2, 4)}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-t">
                      <td className="text-left pr-2 font-medium">Revenue</td>
                      {data.liveDerivation.monthlyRevenue.map((m) => (
                        <td key={m.month} className="px-1.5 text-right font-mono">{fmtGbp(m.revenue)}</td>
                      ))}
                    </tr>
                    <tr className="border-t">
                      <td className="text-left pr-2 font-medium">Index</td>
                      {data.liveDerivation.monthlyRevenue.map((m) => (
                        <td key={m.month} className={`px-1.5 text-right font-mono ${m.index >= 1.1 ? "text-emerald-700" : m.index <= 0.9 ? "text-rose-700" : ""}`}>{m.index.toFixed(2)}</td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="text-[11px] text-muted-foreground border-t pt-2">
              <b>Still manual inputs</b> (forward-planning, not historic): Scenario A day rate, hire schedule, VAT calendar, opening bank balance.
              Anything in the table above feeds the forecast model directly — Aug/Sep/Oct etc. will use the seasonality factor of that month's historical equivalent.
            </div>
          </CardContent>
        </Card>
      )}

      {/* Headline scenarios comparison */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <ScenarioCard label={`Scenario A — £${data.scenarios.a.dayRate}/day (your input)`} summary={sA} accent="amber" />
        <ScenarioCard label={`Scenario B — £${data.scenarios.b.dayRate}/day (live actual)`} summary={sB} accent="emerald" />
      </div>

      {/* Active scenario detail */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Calendar className="h-4 w-4" /> Monthly P&L — Scenario {scenario} · {active.scenarioLabel}
          </CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="text-sm w-full">
            <thead className="text-left text-muted-foreground border-b">
              <tr>
                <th className="py-1">Month</th>
                <th className="text-right">Revenue</th>
                <th className="text-right">Materials</th>
                <th className="text-right">Field labour & vans</th>
                <th className="text-right">Gross profit</th>
                <th className="text-right">GM %</th>
                <th className="text-right">Marketing</th>
                <th className="text-right">Overheads</th>
                <th className="text-right">Net profit (EBITDA)</th>
                <th className="text-right">Net %</th>
                <th className="text-right">Closing cash</th>
              </tr>
            </thead>
            <tbody>
              {active.monthly.map((m) => (
                <tr key={m.monthKey} className="border-t">
                  <td className="py-1.5 pr-2 font-medium">{m.monthLabel}</td>
                  <td className="text-right font-mono">{fmtGbp(m.revenue)}</td>
                  <td className="text-right font-mono">{fmtGbp(m.materials)}</td>
                  <td className="text-right font-mono">{fmtGbp(m.fieldLabourAndVans)}</td>
                  <td className="text-right font-mono font-semibold">{fmtGbp(m.grossProfit)}</td>
                  <td className="text-right font-mono text-muted-foreground">{fmtPct(m.grossMarginPct)}</td>
                  <td className="text-right font-mono">{fmtGbp(m.marketing)}</td>
                  <td className="text-right font-mono">{fmtGbp(m.otherOverheadsPlusOfficePayroll)}</td>
                  <td className={`text-right font-mono font-bold ${m.netProfit >= 0 ? "text-emerald-700" : "text-red-700"}`}>
                    {fmtGbp(m.netProfit)}
                  </td>
                  <td className="text-right font-mono text-muted-foreground">{fmtPct(m.netMarginPct)}</td>
                  <td className="text-right font-mono">{fmtGbp(m.closingBalance)}</td>
                </tr>
              ))}
              <tr className="border-t-2 bg-muted/30 font-semibold">
                <td className="py-1.5 pr-2">Full year</td>
                <td className="text-right font-mono">{fmtGbp(active.summary.fullYearRevenue)}</td>
                <td colSpan={2}></td>
                <td className="text-right font-mono">{fmtGbp(active.summary.fullYearGrossProfit)}</td>
                <td colSpan={2}></td>
                <td></td>
                <td className="text-right font-mono">{fmtGbp(active.summary.fullYearNetProfit)}</td>
                <td></td>
                <td className="text-right font-mono">{fmtGbp(active.summary.closingBankBalance)}</td>
              </tr>
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Weekly cashflow detail */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Receipt className="h-4 w-4" /> Weekly cashflow — Scenario {scenario}
          </CardTitle>
          <p className="text-xs text-muted-foreground">
            Week-by-week movement from the model start ({active.summary.modelStartDate}) to year-end ({active.summary.modelEndDate}).
          </p>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="text-xs w-full">
            <thead className="text-left text-muted-foreground border-b">
              <tr>
                <th className="py-1 sticky left-0 bg-background">Week</th>
                <th className="text-right">Engineers</th>
                <th className="text-right">Chrg days</th>
                <th className="text-right">Existing receipts</th>
                <th className="text-right">New billings</th>
                <th className="text-right">Total receipts</th>
                <th className="text-right">Wages</th>
                <th className="text-right">PAYE</th>
                <th className="text-right">VAT</th>
                <th className="text-right">Materials</th>
                <th className="text-right">Marketing</th>
                <th className="text-right">New eng cost</th>
                <th className="text-right">Loans/Overheads</th>
                <th className="text-right">Total pay</th>
                <th className="text-right">Net Δ</th>
                <th className="text-right">Closing bal</th>
              </tr>
            </thead>
            <tbody>
              {active.weeks.map((w) => {
                const otherCosts = w.amexRepayment + w.barclaycardRepayment + w.barclaysLoan + w.pipeGoCardless + w.otherOverheads + w.directorDividends;
                const matsAll = w.materialsExisting + w.materialsNew;
                const mktAll = w.marketingExisting + w.marketingNew;
                return (
                  <tr key={w.weekCommencing} className="border-t">
                    <td className="py-1 pr-2 sticky left-0 bg-background font-mono">W{w.weekNumber} · {fmtWk(w.weekCommencing)}</td>
                    <td className="text-right font-mono">{w.activeEngineers}</td>
                    <td className="text-right font-mono">{w.chargeableDays.toFixed(1)}</td>
                    <td className="text-right font-mono">{fmtGbp(w.existingReceipts)}</td>
                    <td className="text-right font-mono">{fmtGbp(w.newEngineerBillings)}</td>
                    <td className="text-right font-mono font-semibold">{fmtGbp(w.totalReceipts)}</td>
                    <td className="text-right font-mono">{fmtGbp(w.netWagesExisting)}</td>
                    <td className="text-right font-mono">{fmtGbp(w.payeNicExisting)}</td>
                    <td className={`text-right font-mono ${w.vatPayment > 0 ? "text-rose-700 font-semibold" : ""}`}>{fmtGbp(w.vatPayment)}</td>
                    <td className="text-right font-mono">{fmtGbp(matsAll)}</td>
                    <td className="text-right font-mono">{fmtGbp(mktAll)}</td>
                    <td className="text-right font-mono">{fmtGbp(w.newEngineerLoadedCost)}</td>
                    <td className="text-right font-mono">{fmtGbp(otherCosts)}</td>
                    <td className="text-right font-mono">{fmtGbp(w.totalPayments)}</td>
                    <td className={`text-right font-mono ${w.netMovement >= 0 ? "text-emerald-700" : "text-red-700"}`}>{fmtGbp(w.netMovement)}</td>
                    <td className="text-right font-mono font-bold">{fmtGbp(w.closingBalance)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Card className="border-amber-500/40">
        <CardContent className="p-3 text-xs flex items-start gap-2 text-amber-700 dark:text-amber-300">
          <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
          <div>
            <b>Assumptions held in code</b> at <code className="font-mono">apps/api/src/finance/forecast-config.ts</code>.
            Edit there to re-tune; no DB migration needed. Live opening-balance + actual receipts integration with Xero/Commusoft data is the next phase.
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function ScenarioCard({
  label, summary, accent,
}: {
  label: string;
  summary: ForecastSummary;
  accent: "amber" | "emerald";
}) {
  const border = accent === "emerald" ? "border-emerald-500/40" : "border-amber-500/40";
  return (
    <Card className={border}>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">{label}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <div className="grid grid-cols-2 gap-3">
          <Metric icon={<Wallet className="h-3.5 w-3.5" />} label="Closing bank balance" value={fmtGbp(summary.closingBankBalance)} />
          <Metric icon={<TrendingUp className="h-3.5 w-3.5" />} label="Full-year net profit" value={fmtGbp(summary.fullYearNetProfit)} />
          <Metric icon={<Receipt className="h-3.5 w-3.5" />} label="Full-year revenue" value={fmtGbp(summary.fullYearRevenue)} />
          <Metric icon={<TrendingUp className="h-3.5 w-3.5" />} label="Full-year gross profit" value={fmtGbp(summary.fullYearGrossProfit)} />
        </div>
        <div className="text-[11px] text-muted-foreground pt-1">
          Opening: {fmtGbp(summary.openingBankBalance)} · {summary.totalWeeks} weeks · {summary.modelStartDate} → {summary.modelEndDate}
        </div>
      </CardContent>
    </Card>
  );
}

function Metric({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div>
      <div className="text-muted-foreground text-xs flex items-center gap-1.5">{icon} {label}</div>
      <div className="text-lg font-bold mt-0.5">{value}</div>
    </div>
  );
}

function DerivedRow({
  label, total, liveValue, ssValue, delta, pctMode = false, source = "LIVE", note,
}: {
  label: string;
  total: number;
  liveValue: string;
  ssValue: string;
  delta: string;
  pctMode?: boolean;
  source?: "LIVE" | "FALLBACK";
  note?: string;
}) {
  const deltaNum = Number(delta);
  const deltaCls = deltaNum > 0 ? "text-emerald-700" : deltaNum < 0 ? "text-rose-700" : "text-muted-foreground";
  const fmtMoney = (n: number) => `£${n.toLocaleString("en-GB", { maximumFractionDigits: 0 })}`;
  const fmtDelta = pctMode ? `${deltaNum >= 0 ? "+" : ""}${delta}pp` : `${deltaNum >= 0 ? "+" : ""}${fmtMoney(Math.round(deltaNum))}`;
  return (
    <tr className="border-t">
      <td className="py-1 pr-2 font-medium">
        {label}
        <span className={`ml-1.5 text-[9px] font-bold px-1 py-0.5 rounded ${source === "LIVE" ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300" : "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300"}`}>
          {source}
        </span>
        {note && <div className="text-[10px] text-muted-foreground font-normal">{note}</div>}
      </td>
      <td className="text-right font-mono pr-2">{fmtMoney(total)}</td>
      <td className="text-right font-mono pr-2 text-muted-foreground">{fmtMoney(Math.round(total / 12))}</td>
      <td className="text-right font-mono font-bold pr-2">{liveValue}</td>
      <td className="text-right font-mono text-muted-foreground pr-2">{ssValue}</td>
      <td className={`text-right font-mono text-xs ${deltaCls}`}>{source === "LIVE" ? fmtDelta : "—"}</td>
    </tr>
  );
}
